import React, { useEffect, useCallback, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import FocusLock from 'react-focus-lock';
import { useIntl } from 'react-intl';

import { ForegroundImage } from '../../../ContentfulTypes';
import {
  PageScrollStyles,
  OuterBladeContainer,
  BladeContent,
  Overlay,
  CloseBtn,
  CloseIcon,
  Legend,
  LogoContainer,
  StyledImage,
  NavLink,
} from './styles';

interface Props {
  bladeState: boolean;
  setBladeState: () => void;
  addDivider?: boolean;
  bladeWidth: number;
  legendLeftButton?: ReactNode;
  hideHeader?: boolean;
  noPadding?: boolean;
  onClose?: () => void;
  showClose?: boolean;
  children?: ReactNode;
  logo?: ForegroundImage;
}

const BladeContainer = ({
  bladeState,
  setBladeState,
  addDivider = false,
  legendLeftButton,
  bladeWidth,
  children,
  hideHeader = false,
  noPadding = false,
  onClose = () => null,
  showClose = true,
  logo,
}: Props) => {
  const { locale } = useIntl();
  const closeLabel = locale === 'en' ? 'Close' : 'Ferme';

  // Get the div that will be used as a portal
  const portalRoot = typeof document !== 'undefined' && document.getElementById('portal');
  // User hits "esc" button
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && showClose) {
      setBladeState(false);
    }
  }, [setBladeState, showClose]);

  // Enable or disable page scrolling (outside of the blade)
  const setPageScroll = (pageScroll) => {
    const root = document?.documentElement;
    const gatsbyRoot = document.getElementById('___gatsby');
    if (root) {
      if (!pageScroll) {
        root.classList.add('js-blade-open');
        if (gatsbyRoot) {
          gatsbyRoot.setAttribute('aria-hidden', 'true');
        }
      } else {
        root.classList.remove('js-blade-open');
        if (gatsbyRoot) {
          gatsbyRoot.removeAttribute('aria-hidden');
        }
      }
    }
  };
  
  // Add keyboard listener for closing blade
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, false);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, false);
      // On unmount of the blade, enable page scrolling
      setPageScroll(true);
    };
  }, [handleKeyDown]);
  
  // When the blade is open, disable page scrolling. When the blade is closed, enable page scrolling
  useEffect(() => setPageScroll(!bladeState), [bladeState]);
    
  if (!portalRoot) {
    return null;
  }

  const closeBlade = () => {
    onClose();
    setBladeState(false);
  };

  const portalContent = (
    <CSSTransition
      in={bladeState}
      timeout={{
        enter: 300,
        exit: 700,
      }}
      classNames="on-blade"
      unmountOnExit
    >
      <>
        <OuterBladeContainer
          className="blade-scroll-container"
          bladeWidth={bladeWidth}
          noPadding={noPadding}
        >
          <PageScrollStyles />
          {/* Content */}
          <BladeContent bladeWidth={bladeWidth}>
            <FocusLock returnFocus className="focus-lock">
              {!hideHeader && (
                <Legend bladeWidth={bladeWidth}>
                  {legendLeftButton}

                  {/* Logo dispaly on mobile only */}
                  <LogoContainer bladeWidth={bladeWidth}>
                    <NavLink to={`/${locale}`} tabIndex="0">
                      <StyledImage image={logo} />
                    </NavLink>
                  </LogoContainer>

                  {/* "Close" text displays on desktop only, "X" displays always */}
                  {showClose && (
                    <CloseBtn onClick={closeBlade} bladeWidth={bladeWidth}>
                      <span id="closeText">{closeLabel}</span>
                      <CloseIcon>
                        <span className="bm-cross-left" />
                        <span className="bm-cross-right" />
                      </CloseIcon>
                    </CloseBtn>
                  )}
                </Legend>
              )}

              {/* Optional HR divider */}
              {addDivider && <hr className="closeHR" />}

              {/* Children go in here */}
              {children}
            </FocusLock>
          </BladeContent>
        </OuterBladeContainer>
        <Overlay onClick={() => showClose && setBladeState(false)} />
      </>
    </CSSTransition>
  );

  return createPortal(portalContent, portalRoot);
};

export default BladeContainer;
